import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
// import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChurchOutlinedIcon from "@mui/icons-material/ChurchOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import logo from "../../assets/images/main_logo.png";
import ctcaplogo from "../../assets/images/logo.png";
import BASE_URL from "../../components/config/appconfig";
import QuestionEditor from "../../components/questionEditor/questionEditor";
import UserList from "../../components/userList/userList";
import usePageMeta from "../../utils/usePageMeta";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ── Design tokens ──────────────────────────────────────────────
const C = {
  deepBlack: "#263842",
  slate: "#50545D",
  ocean: "#9AA4AF",
  pacific: "#009CCC",
  pacificBg: "#E2F8FF",
  border: "#E4E4E7",
  pageBg: "#F9FAFB",
  white: "#FFFFFF",
  sidebar: "#FFFFFF",
};

const COLUMNS = [
  { label: "Full Name", field: "full_name" },
  { label: "Email", field: "email" },
  { label: "Church Name", field: "church_name" },
  { label: "City / Country", field: "city" },
  { label: "Church Age", field: "church_age" },
  { label: "Payment Status", field: "payment_status" },
  { label: "Framework Status", field: "assessment_status" },
  { label: "Completed Date", field: "completed_at" },
];

const SortIcon = ({ field, sortField, sortDir }) => {
  const isActive = sortField === field;
  const Icon = isActive
    ? sortDir === "asc"
      ? ArrowUpwardIcon
      : ArrowDownwardIcon
    : SwapVertIcon;
  return (
    <Icon
      sx={{
        fontSize: 12,
        cursor: "pointer",
        flexShrink: 0,
        color: isActive ? "#ef2d2d" : "#b0bac4",
      }}
    />
  );
};

const NAV_ITEMS = [
  {
    label: "Dashboard",
    icon: <DashboardOutlinedIcon sx={{ fontSize: 18 }} />,
    key: "dashboard",
  },
  {
    label: "Editor",
    icon: <EditNoteOutlinedIcon sx={{ fontSize: 18 }} />,
    key: "editor",
  },
  {
    label: "User List",
    icon: <PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />,
    key: "userlist",
  },
];

const STAT_ICONS = [
  <GroupsOutlinedIcon sx={{ fontSize: 22, color: C.pacific }} />,
  <ChurchOutlinedIcon sx={{ fontSize: 22, color: C.pacific }} />,
  <BarChartOutlinedIcon sx={{ fontSize: 22, color: C.pacific }} />,
  <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 22, color: C.pacific }} />,
];

// ── Section title ──────────────────────────────────────────────
const SectionTitle = ({ children, sub }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      sx={{
        fontSize: { xs: 11, md: 13 },
        fontWeight: 700,
        color: C.deepBlack,
        letterSpacing: 0.3,
      }}
    >
      {children}
    </Typography>
    {sub && (
      <Typography
        sx={{ fontSize: { xs: 9, md: 11 }, color: C.ocean, mt: 0.25 }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

// ── Card wrapper ───────────────────────────────────────────────
const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: C.white,
      borderRadius: "10px",
      border: `1px solid ${C.border}`,
      width: "100%",
      minWidth: 0,
      overflow: "hidden",
      boxSizing: "border-box",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// ── Main component ─────────────────────────────────────────────
const AdminDashboard = () => {
  usePageMeta(
    "Admin Dashboard",
    "Manage the Gospel and Church Health Framework and users.",
  );
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("userData"));
  const userName = userData?.full_name || "Admin User";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeNav, setActiveNav] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [timeFilter, setTimeFilter] = useState("week");
  const [timeFilterAnchor, setTimeFilterAnchor] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [statusFilter, setStatusFilter] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState([]);
  const [dashFilter, setDashFilter] = useState("all");
  const [dashLocation, setDashLocation] = useState("");
  const [dashLocationAnchor, setDashLocationAnchor] = useState(null);
  const [adminRevoked, setAdminRevoked] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const getInitials = (name) => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
  };

  const fetchDashboardStats = () => {
    fetch(`${BASE_URL}/api/admin/dashboard-stats`)
      .then((r) => r.json())
      .then((result) => {
        if (result.success) setStats(result.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/check-role`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.success) return;
        if (!data.is_admin || data.role !== "admin") {
          setAdminRevoked(true);
          clearInterval(interval);
        }
      } catch {
        // ignore network errors
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleRevokedOk = async () => {
    await fetch(`${BASE_URL}/api/admin/logout`, { method: "POST" });
    localStorage.removeItem("userData");
    localStorage.removeItem("pendingUser");
    navigate("/sign-in");
  };

  const handleLogout = async () => {
    await fetch(`${BASE_URL}/api/admin/logout`, { method: "POST" });
    localStorage.removeItem("userData");
    localStorage.removeItem("pendingUser");
    navigate("/sign-in");
  };

  // ── Participant filters ──
  const isThisQuarter = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      Math.floor(d.getMonth() / 3) === Math.floor(now.getMonth() / 3)
    );
  };

  const allParticipants = stats?.participants || [];
  const locations = [
    ...new Set(allParticipants.map((p) => p.city).filter(Boolean)),
  ].sort();

  // ── Dashboard-level filter (top-right) ──
  const dashFilteredParticipants = allParticipants.filter((p) => {
    if (dashFilter === "quarter") return isThisQuarter(p.created_at);
    if (dashFilter === "location")
      return !dashLocation || p.city === dashLocation;
    return true;
  });

  const isLastWeek = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    return d >= weekAgo && d <= now;
  };

  const isLastMonth = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    const monthAgo = new Date(now);
    monthAgo.setMonth(now.getMonth() - 1);
    return d >= monthAgo && d <= now;
  };

  const TIME_LABELS = {
    all: "All Time",
    week: "Last Week",
    month: "Last Month",
    quarter: "This Quarter",
  };

  const filteredParticipants = dashFilteredParticipants.filter((p) => {
    if (timeFilter === "week" && !isLastWeek(p.created_at)) return false;
    if (timeFilter === "month" && !isLastMonth(p.created_at)) return false;
    if (timeFilter === "quarter" && !isThisQuarter(p.created_at)) return false;
    if (statusFilter.length > 0 && !statusFilter.includes(p.assessment_status))
      return false;
    if (paymentFilter.length > 0 && !paymentFilter.includes(p.payment_status))
      return false;
    return true;
  });

  const hasActiveFilter = statusFilter.length > 0 || paymentFilter.length > 0;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedParticipants = [...filteredParticipants].sort((a, b) => {
    if (!sortField) return 0;
    if (sortField === "completed_at") {
      const aT = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const bT = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return sortDir === "asc" ? aT - bT : bT - aT;
    }
    if (sortField === "church_age") {
      const aT = parseFloat(a.church_age) || 0;
      const bT = parseFloat(b.church_age) || 0;
      return sortDir === "asc" ? aT - bT : bT - aT;
    }
    const aVal = (a[sortField] ?? "").toString().toLowerCase();
    const bVal = (b[sortField] ?? "").toString().toLowerCase();
    if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleFilter = (arr, setArr, val) =>
    setArr((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );

  const exportCSV = () => {
    const headers = [
      "Full Name",
      "Email",
      "Church Name",
      "City",
      "Country",
      "Church Age",
      "CTC Status",
      "Framework Status",
      "Completed Date",
    ];
    const rows = filteredParticipants.map((p) => [
      p.full_name,
      p.email,
      p.church_name,
      p.city,
      p.country,
      p.church_age,
      p.ctc_status === "user" ? "User" : "Contact",
      p.assessment_status,
      p.completed_at ? formatDate(p.completed_at) : "—",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${v ?? ""}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "participants.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const isFiltered = dashFilter !== "all";
  const dashTotal = dashFilteredParticipants.length;
  const dashChurches = new Set(
    dashFilteredParticipants.map((p) => p.church_name).filter(Boolean),
  ).size;
  const dashCompleted = dashFilteredParticipants.filter(
    (p) => p.assessment_status === "completed",
  ).length;
  const dashPayment = dashFilteredParticipants.filter(
    (p) => p.payment_status === "verified",
  ).length;
  const dashStarted = dashFilteredParticipants.filter(
    (p) => p.assessment_status !== "not_started",
  ).length;

  // ── Funnel ──
  const funnel = stats?.conversionFunnel || {};
  const funnelSignup = isFiltered ? dashTotal : funnel.signup || 0;
  const funnelPayment = isFiltered ? dashPayment : funnel.payment || 0;
  const funnelStarted = isFiltered ? dashStarted : funnel.started || 0;
  const funnelDone = isFiltered ? dashCompleted : funnel.completed || 0;
  const funnelMax = funnelSignup || 1;
  const funnelRows = [
    { label: "Signup", value: funnelSignup, color: C.pacific },
    { label: "Payment", value: funnelPayment, color: "rgba(0,156,204,0.6)" },
    { label: "Started", value: funnelStarted, color: "rgba(0,156,204,0.4)" },
    { label: "Completed", value: funnelDone, color: "rgba(0,156,204,0.2)" },
  ];

  // ── Engagement chart ──
  const engagementData = {
    labels: stats?.engagementTrends?.map((w) => w.label) || [],
    datasets: [
      {
        data: stats?.engagementTrends?.map((w) => w.count) || [],
        backgroundColor:
          stats?.engagementTrends?.map((_, i) =>
            i % 2 === 1 ? C.pacific : "rgba(196,196,196,0.3)",
          ) || [],
        borderRadius: 4,
        barPercentage: 0.55,
        categoryPercentage: 0.7,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw} submissions` } },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: C.ocean, font: { size: 11 } },
      },
      y: {
        grid: { color: "#F0F1F3" },
        border: { display: false },
        ticks: { color: C.ocean, font: { size: 11 }, stepSize: 1 },
      },
    },
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const statCards = [
    {
      label: "Total Respondents",
      value: isFiltered ? dashTotal : (stats?.totalRespondents ?? "—"),
      icon: STAT_ICONS[0],
    },
    {
      label: "Total Churches",
      value: isFiltered ? dashChurches : (stats?.totalChurches ?? "—"),
      icon: STAT_ICONS[1],
    },
    {
      label: "Health Score Avg",
      value:
        stats?.healthScoreAvg != null ? `${stats.healthScoreAvg} / 5` : "—",
      icon: STAT_ICONS[2],
    },
    {
      label: "Completed Audits",
      value: isFiltered ? dashCompleted : (stats?.completedAudits ?? "—"),
      icon: STAT_ICONS[3],
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        bgcolor: C.pageBg,
        overflow: "hidden",
      }}
    >
      {/* ══ Mobile Drawer ════════════════════════════════════════ */}
      <Drawer
        anchor="left"
        open={isMobile && sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        slotProps={{ paper: { sx: { width: 230 } } }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            py: "24px",
            bgcolor: C.sidebar,
          }}
        >
          <Box
            sx={{
              px: "16px",
              mb: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{ height: 22, objectFit: "contain" }}
            />
            <IconButton
              onClick={() => setSidebarOpen(false)}
              size="small"
              sx={{ color: C.slate }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1 }}>
            {NAV_ITEMS.map((item) => {
              const isActive = activeNav === item.key;
              return (
                <Box
                  key={item.key}
                  onClick={() => {
                    setActiveNav(item.key);
                    setSidebarOpen(false);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    px: "24px",
                    py: "12px",
                    cursor: "pointer",
                    bgcolor: isActive ? "#FFF0F0" : "transparent",
                    borderLeft: isActive
                      ? "3px solid #EF2D2D"
                      : "3px solid transparent",
                    color: isActive ? "#EF2D2D" : C.ocean,
                    "&:hover": {
                      bgcolor: C.pageBg,
                      color: isActive ? "#EF2D2D" : C.slate,
                    },
                    transition: "all 0.15s",
                  }}
                >
                  {item.icon}
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      color: "inherit",
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ borderTop: `1px solid ${C.border}`, pt: "12px" }}>
            <Box
              onClick={handleLogout}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                px: "24px",
                py: "10px",
                cursor: "pointer",
                color: C.ocean,
                "&:hover": { color: "#EF2D2D" },
              }}
            >
              <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13 }}>Logout</Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ══ Sidebar (desktop) ════════════════════════════════════ */}
      <Box
        sx={{
          width: 230,
          flexShrink: 0,
          bgcolor: C.sidebar,
          borderRight: `1px solid ${C.border}`,
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          pt: "24px",
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            px: "24px",
            mb: "32px",
            justifyContent: "center",
            display: "flex",
          }}
        >
          <Box component="img" src={logo} alt="Logo" sx={{ height: 32 }} />
        </Box>

        {/* Nav items */}
        <Box sx={{ flex: 1 }}>
          {NAV_ITEMS.map((item) => {
            const isActive = activeNav === item.key;
            return (
              <Box
                key={item.key}
                onClick={() => setActiveNav(item.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  px: "24px",
                  py: "12px",
                  cursor: "pointer",
                  bgcolor: isActive ? "#FFF0F0" : "transparent",
                  borderLeft: isActive
                    ? "3px solid #EF2D2D"
                    : "3px solid transparent",
                  color: isActive ? "#EF2D2D" : C.ocean,
                  "&:hover": {
                    bgcolor: C.pageBg,
                    color: isActive ? "#EF2D2D" : C.slate,
                  },
                  transition: "all 0.15s",
                }}
              >
                {item.icon}
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 400,
                    color: "inherit",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Bottom */}
        <Box sx={{ borderTop: `1px solid ${C.border}` }}>
          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              px: "24px",
              py: "10px",
              cursor: "pointer",
              color: C.ocean,
              "&:hover": { color: C.slate },
            }}
          >
            <SupportAgentOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 13 }}>Support</Typography>
          </Box> */}
          <Box
            onClick={handleLogout}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              px: "24px",
              py: "24px",
              cursor: "pointer",
              color: C.ocean,
              "&:hover": { color: "#EF2D2D" },
            }}
          >
            <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 13 }}>Logout</Typography>
          </Box>
        </Box>
      </Box>

      {/* ══ Main area ════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: "16px", md: "32px" },
            height: "64px",
            bgcolor: C.white,
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
            gap: "10px",
          }}
        >
          {/* Hamburger — mobile only */}
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              display: { xs: "flex", lg: "none" },
              color: C.slate,
              p: "6px",
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            sx={{
              fontSize: { xs: 11, md: 13 },
              fontWeight: 700,
              letterSpacing: { xs: "0.5px", md: "1.5px" },
              color: C.deepBlack,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {
              {
                dashboard: "Admin Dashboard",
                editor: "Question Editor",
                userlist: "User List",
              }[activeNav]
            }
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
            }}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: C.pacificBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 13,
                color: C.pacific,
                border: `1.5px solid ${C.pacific}`,
                flexShrink: 0,
              }}
            >
              {getInitials(userName)}
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.deepBlack,
                  lineHeight: 1.2,
                }}
              >
                {userName}
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.ocean }}>
                Administrator
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleLogout();
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Box>

        {/* Scrollable content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            p: activeNav === "dashboard" ? { xs: "16px", md: "32px" } : "0",
            minWidth: 0,
          }}
        >
          {activeNav === "editor" && <QuestionEditor />}
          {activeNav === "userlist" && (
            <UserList onDeleteSuccess={fetchDashboardStats} />
          )}

          {activeNav === "dashboard" && (
            <>
              {/* Page heading + filters */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", md: "flex-start" },
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  gap: { xs: "12px", md: "0" },
                  mb: "28px",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 14, md: 20 },
                      fontWeight: 700,
                      color: C.deepBlack,
                      letterSpacing: 0.2,
                    }}
                  >
                    GCCHF Analytics Dashboard
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 10, md: 13 },
                      color: C.ocean,
                      mt: "4px",
                    }}
                  >
                    The Gospel and Church Health Framework across regional
                    networks.
                  </Typography>
                </Box>

                {/* Filter buttons */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    mt: "4px",
                  }}
                >
                  {/* This Quarter */}
                  <Box
                    onClick={() =>
                      setDashFilter(
                        dashFilter === "quarter" ? "all" : "quarter",
                      )
                    }
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: dashFilter === "quarter" ? C.white : C.slate,
                      bgcolor: dashFilter === "quarter" ? C.pacific : C.white,
                      border: `1px solid ${
                        dashFilter === "quarter" ? C.pacific : C.border
                      }`,
                      borderRadius: "10px",
                      px: "14px",
                      py: "7px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        bgcolor:
                          dashFilter === "quarter" ? C.pacific : "#F0F1F3",
                      },
                    }}
                  >
                    This Quarter
                    <Box component="span" sx={{ fontSize: 10, opacity: 0.7 }}>
                      ▼
                    </Box>
                  </Box>

                  {/* Location */}
                  <Box
                    onClick={(e) => {
                      setDashFilter("location");
                      setDashLocationAnchor(e.currentTarget);
                    }}
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: dashFilter === "location" ? C.white : C.slate,
                      bgcolor: dashFilter === "location" ? C.pacific : C.white,
                      border: `1px solid ${
                        dashFilter === "location" ? C.pacific : C.border
                      }`,
                      borderRadius: "10px",
                      px: "14px",
                      py: "7px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        bgcolor:
                          dashFilter === "location" ? C.pacific : "#F0F1F3",
                      },
                    }}
                  >
                    {dashLocation || "Location"}
                    <Box component="span" sx={{ fontSize: 10, opacity: 0.7 }}>
                      ▼
                    </Box>
                  </Box>
                  <Menu
                    anchorEl={dashLocationAnchor}
                    open={Boolean(dashLocationAnchor)}
                    onClose={() => setDashLocationAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setDashLocation("");
                        setDashFilter("all");
                        setDashLocationAnchor(null);
                      }}
                    >
                      All Locations
                    </MenuItem>
                    {locations.map((loc) => (
                      <MenuItem
                        key={loc}
                        selected={dashLocation === loc}
                        onClick={() => {
                          setDashLocation(loc);
                          setDashLocationAnchor(null);
                        }}
                      >
                        {loc}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Box>

              {loading ? (
                <LinearProgress sx={{ borderRadius: 1, color: C.pacific }} />
              ) : (
                <>
                  {/* ── Stat Cards ── */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, 1fr)",
                        md: "repeat(4, 1fr)",
                      },
                      gap: { xs: "12px", md: "20px" },
                      mb: "24px",
                    }}
                  >
                    {statCards.map((card) => (
                      <Card
                        key={card.label}
                        sx={{ p: { xs: "12px", md: "24px" } }}
                      >
                        {/* Icon */}
                        <Box
                          sx={{
                            width: { xs: 30, md: 44 },
                            height: { xs: 30, md: 44 },
                            borderRadius: "10px",
                            bgcolor: C.pacificBg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: { xs: "8px", md: "16px" },
                          }}
                        >
                          {card.icon}
                        </Box>
                        {/* Label */}
                        <Typography
                          sx={{
                            fontSize: { xs: 9, md: 11 },
                            fontWeight: 600,
                            color: C.ocean,
                            letterSpacing: "0.8px",
                            textTransform: "uppercase",
                            mb: "6px",
                          }}
                        >
                          {card.label}
                        </Typography>
                        {/* Value */}
                        <Typography
                          sx={{
                            fontSize: { xs: 22, md: 36 },
                            fontWeight: 900,
                            color: C.deepBlack,
                            lineHeight: 1,
                            letterSpacing: "-0.5px",
                          }}
                        >
                          {card.value}
                        </Typography>
                      </Card>
                    ))}
                  </Box>

                  {/* ── Conversion Funnel + Engagement Trends ── */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: "20px",
                      mb: "24px",
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    {/* Conversion Funnel */}
                    <Card sx={{ p: { xs: "14px", md: "24px" } }}>
                      <SectionTitle sub="Participant journey through the Framework pipeline">
                        Conversion Funnel
                      </SectionTitle>
                      {funnelRows.map((row) => {
                        const pct =
                          funnelMax > 0
                            ? Math.round((row.value / funnelMax) * 100)
                            : 0;
                        return (
                          <Box key={row.label} sx={{ mb: "18px" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: "6px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: 10, md: 12 },
                                  fontWeight: 500,
                                  color: C.slate,
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {row.label}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: { xs: 10, md: 12 },
                                    fontWeight: 700,
                                    color: C.deepBlack,
                                  }}
                                >
                                  {row.value.toLocaleString()}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: { xs: 9, md: 11 },
                                    color: C.ocean,
                                    minWidth: 32,
                                    textAlign: "right",
                                  }}
                                >
                                  {pct}%
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                height: 10,
                                width: "100%",
                                bgcolor: "#F0F1F3",
                                borderRadius: "5px",
                                overflow: "hidden",
                                boxSizing: "border-box",
                              }}
                            >
                              <Box
                                sx={{
                                  height: "100%",
                                  width: `${pct}%`,
                                  bgcolor: row.color,
                                  borderRadius: "5px",
                                  transition: "width 0.7s ease",
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Card>

                    {/* Engagement Trends */}
                    <Card sx={{ p: { xs: "14px", md: "24px" } }}>
                      <SectionTitle sub="New audit starts over the last 4 weeks">
                        Engagement Trends
                      </SectionTitle>
                      <Box
                        sx={{
                          height: { xs: 160, md: 180 },
                          mt: "8px",
                          width: "100%",
                          minWidth: 0,
                          position: "relative",
                        }}
                      >
                        <Bar data={engagementData} options={chartOptions} />
                      </Box>
                    </Card>
                  </Box>

                  {/* ── Participant Management ── */}
                  <Card sx={{ overflow: "hidden" }}>
                    {/* Header */}
                    <Box
                      sx={{
                        px: { xs: "16px", md: "24px" },
                        py: "20px",
                        borderBottom: `1px solid ${C.border}`,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: { xs: "12px", sm: "0" },
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: C.slate,
                            letterSpacing: "1.5px",
                          }}
                        >
                          Participant Management
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: C.ocean,
                            mt: "2px",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Detailed breakdown of Framework participants
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        {/* Filter button */}
                        <Box
                          onClick={(e) => setFilterAnchor(e.currentTarget)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            border: `1px solid ${
                              hasActiveFilter ? C.pacific : C.border
                            }`,
                            borderRadius: "8px",
                            px: "14px",
                            py: "7px",
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 500,
                            color: hasActiveFilter ? C.pacific : C.slate,
                            bgcolor: hasActiveFilter ? C.pacificBg : C.white,
                            "&:hover": { bgcolor: C.pageBg },
                          }}
                        >
                          <TuneIcon sx={{ fontSize: 14 }} /> FILTER{" "}
                          {hasActiveFilter &&
                            `(${statusFilter.length + paymentFilter.length})`}
                        </Box>
                        <Menu
                          anchorEl={filterAnchor}
                          open={Boolean(filterAnchor)}
                          onClose={() => setFilterAnchor(null)}
                          slotProps={{ paper: { sx: { p: 1, minWidth: 200 } } }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.ocean,
                              px: 1.5,
                              py: 0.5,
                              letterSpacing: 1,
                            }}
                          >
                            Framework Status
                          </Typography>
                          {[
                            ["completed", "Completed"],
                            ["in_progress", "In Progress"],
                            ["not_started", "Inactive"],
                          ].map(([val, label]) => (
                            <MenuItem
                              key={val}
                              onClick={() => {
                                toggleFilter(
                                  statusFilter,
                                  setStatusFilter,
                                  val,
                                );
                                setFilterAnchor(null); // ✅ Close menu after selection
                              }}
                              sx={{ fontSize: 12, py: "2px" }}
                            >
                              <Checkbox
                                checked={statusFilter.includes(val)}
                                size="small"
                                sx={{ p: "4px", mr: 1 }}
                              />
                              {label}
                            </MenuItem>
                          ))}
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.ocean,
                              px: 1.5,
                              py: 0.5,
                              mt: 1,
                              letterSpacing: 1,
                            }}
                          >
                            Payment Status
                          </Typography>
                          {[
                            ["verified", "Verified"],
                            ["pending", "Pending"],
                          ].map(([val, label]) => (
                            <MenuItem
                              key={val}
                              onClick={() => {
                                toggleFilter(
                                  paymentFilter,
                                  setPaymentFilter,
                                  val,
                                );
                                setFilterAnchor(null); // ✅ Close menu
                              }}
                              sx={{ fontSize: 12, py: "2px" }}
                            >
                              <Checkbox
                                checked={paymentFilter.includes(val)}
                                size="small"
                                sx={{ p: "4px", mr: 1 }}
                              />
                              {label}
                            </MenuItem>
                          ))}
                          <Box
                            sx={{
                              px: 1.5,
                              pt: 1,
                              borderTop: `1px solid ${C.border}`,
                              mt: 1,
                            }}
                          >
                            <Box
                              onClick={() => {
                                setStatusFilter([]);
                                setPaymentFilter([]);
                                setFilterAnchor(null);
                              }}
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: C.pacific,
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              Clear filters
                            </Box>
                          </Box>
                        </Menu>

                        {/* Time filter dropdown */}
                        <Box
                          onClick={(e) => setTimeFilterAnchor(e.currentTarget)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            border: `1px solid ${C.border}`,
                            borderRadius: "8px",
                            px: "14px",
                            py: "7px",
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 500,
                            color: C.slate,
                            bgcolor: C.white,
                            "&:hover": { bgcolor: C.pageBg },
                          }}
                        >
                          <CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />
                          {TIME_LABELS[timeFilter]}
                          <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                        </Box>
                        <Menu
                          anchorEl={timeFilterAnchor}
                          open={Boolean(timeFilterAnchor)}
                          onClose={() => setTimeFilterAnchor(null)}
                        >
                          {Object.entries(TIME_LABELS).map(([key, label]) => (
                            <MenuItem
                              key={key}
                              selected={timeFilter === key}
                              onClick={() => {
                                setTimeFilter(key);
                                setTimeFilterAnchor(null);
                              }}
                            >
                              {label}
                            </MenuItem>
                          ))}
                        </Menu>

                        {/* Export CSV */}
                        <Box
                          onClick={exportCSV}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            borderRadius: "8px",
                            px: "14px",
                            py: "7px",
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 700,
                            color: C.white,
                            bgcolor: C.deepBlack,
                            "&:hover": { bgcolor: "#1a262e" },
                          }}
                        >
                          <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
                          <Box
                            component="span"
                            sx={{ display: { xs: "none", sm: "inline" } }}
                          >
                            Export CSV
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ overflowX: "auto", width: "100%" }}>
                      <TableContainer>
                        <Table
                          size="small"
                          sx={{ tableLayout: "fixed", minWidth: 1000 }}
                        >
                          <colgroup>
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "11%" }} />
                            <col style={{ width: "9%" }} />
                            <col style={{ width: "11%" }} />
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "14%" }} />
                          </colgroup>
                          <TableHead>
                            <TableRow sx={{ bgcolor: C.pageBg }}>
                              {COLUMNS.map(({ label, field }) => (
                                <TableCell
                                  key={field}
                                  onClick={() => handleSort(field)}
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: C.ocean,
                                    letterSpacing: "0.7px",
                                    py: "12px",
                                    borderBottom: `1px solid ${C.border}`,
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                    userSelect: "none",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    {label}
                                    <SortIcon
                                      field={field}
                                      sortField={sortField}
                                      sortDir={sortDir}
                                    />
                                  </Box>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedParticipants.map((p) => {
                              const aStatus = p.assessment_status;
                              const dotColor =
                                aStatus === "completed"
                                  ? "#22c55e"
                                  : aStatus === "in_progress"
                                    ? "#f59e0b"
                                    : "#9ca3af";
                              const aLabel =
                                aStatus === "completed"
                                  ? "COMPLETED"
                                  : aStatus === "in_progress"
                                    ? "IN PROGRESS"
                                    : "NOT STARTED";
                              return (
                                <TableRow
                                  key={p.id}
                                  sx={{
                                    "&:hover": { bgcolor: "#FAFAFA" },
                                    "& td": {
                                      borderBottom: `1px solid ${C.border}`,
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: C.deepBlack,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {p.full_name}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 12,
                                      color: C.slate,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={p.email}
                                  >
                                    {p.email}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 12,
                                      color: C.slate,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {p.church_name}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 12,
                                      color: C.slate,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {[p.city, p.country]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontSize: 12, color: C.slate }}
                                  >
                                    {p.church_age}
                                  </TableCell>
                                  <TableCell>
                                    {p.payment_status &&
                                    p.payment_status !== "-" ? (
                                      <Box
                                        sx={{
                                          display: "inline-block",
                                          px: "10px",
                                          py: "3px",
                                          borderRadius: "6px",
                                          fontSize: 11,
                                          fontWeight: 600,
                                          bgcolor:
                                            p.payment_status === "verified"
                                              ? "#D1FAE5"
                                              : "#FEF3C7",
                                          color:
                                            p.payment_status === "verified"
                                              ? "#065F46"
                                              : "#92400E",
                                        }}
                                      >
                                        {p.payment_status}
                                      </Box>
                                    ) : (
                                      <Typography
                                        sx={{ fontSize: 12, color: C.slate }}
                                      >
                                        —
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          bgcolor: dotColor,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: 11,
                                          fontWeight: 600,
                                          color: C.slate,
                                          letterSpacing: "0.4px",
                                        }}
                                      >
                                        {aLabel}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: 12,
                                      color: C.slate,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {p.completed_at
                                      ? formatDate(p.completed_at)
                                      : "—"}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {!sortedParticipants.length && (
                              <TableRow>
                                <TableCell
                                  colSpan={8}
                                  align="center"
                                  sx={{
                                    py: "32px",
                                    color: C.ocean,
                                    fontSize: 13,
                                  }}
                                >
                                  No participants found
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Card>
                </>
              )}
            </>
          )}
        </Box>

        {/* ── Admin Footer ── */}
        <Box
          sx={{
            borderTop: `1px solid ${C.border}`,
            bgcolor: C.white,
            py: { xs: "12px", md: "22px" },
            px: { xs: "16px", md: "22px" },
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={ctcaplogo}
            alt="Logo"
            sx={{ height: 22, objectFit: "contain" }}
          />
          <Typography sx={{ fontSize: 11, color: C.ocean }}>
            © 2009-{new Date().getFullYear()}, City To City Asia Pacific LTD.
            All rights reserved
          </Typography>
        </Box>
      </Box>

      {adminRevoked && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              p: "32px",
              maxWidth: 400,
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
          >
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: C.deepBlack,
                mb: 1.5,
              }}
            >
              Admin Access Removed
            </Typography>
            <Typography sx={{ fontSize: 14, color: C.slate, mb: 3 }}>
              Your admin privileges have been removed.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box
                onClick={handleRevokedOk}
                sx={{
                  bgcolor: C.pacific,
                  color: "#fff",
                  px: "24px",
                  py: "10px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  userSelect: "none",
                  "&:hover": { bgcolor: "#007aa3" },
                }}
              >
                OK
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdminDashboard;
